import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SupplierPageLayout from '../components/SupplierPageLayout';
import SupplierStatusBadge from '../components/SupplierStatusBadge';
import SupplierEmptyState from '../components/SupplierEmptyState';
import {
  SupplierPoSummary,
  getSupplierDashboard,
} from '../services/supplierApi';

const Dashboard: React.FC = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [dispatchedCount, setDispatchedCount] = useState(0);
  const [linkedRestaurants, setLinkedRestaurants] = useState(0);
  const [recentOrders, setRecentOrders] = useState<SupplierPoSummary[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    getSupplierDashboard()
      .then((data) => {
        setPendingCount(data.pendingCount ?? 0);
        setApprovedCount(data.approvedCount ?? 0);
        setDispatchedCount(data.dispatchedCount ?? 0);
        setLinkedRestaurants(data.linkedRestaurants ?? 0);
        setRecentOrders(data.recentOrders ?? []);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  return (
    <SupplierPageLayout
      wide
      title="Dashboard"
      subtitle="Purchase orders and activity from restaurants linked to your supplier account."
      icon="fa-tachometer"
      breadcrumbs={[{ label: 'Dashboard' }]}
    >
      {error && <div className="sp-alert sp-alert--error">{error}</div>}

      <div className="sp-stats">
        <div className="sp-stat">
          <div className="sp-stat__icon"><i className="fa fa-clock-o" aria-hidden="true" /></div>
          <span className="sp-stat__value">{pendingCount}</span>
          <span className="sp-stat__label">Pending response</span>
        </div>
        <div className="sp-stat">
          <div className="sp-stat__icon"><i className="fa fa-check-circle" aria-hidden="true" /></div>
          <span className="sp-stat__value">{approvedCount}</span>
          <span className="sp-stat__label">Approved</span>
        </div>
        <div className="sp-stat">
          <div className="sp-stat__icon"><i className="fa fa-truck" aria-hidden="true" /></div>
          <span className="sp-stat__value">{dispatchedCount}</span>
          <span className="sp-stat__label">Dispatched</span>
        </div>
        <div className="sp-stat">
          <div className="sp-stat__icon"><i className="fa fa-building-o" aria-hidden="true" /></div>
          <span className="sp-stat__value">{linkedRestaurants}</span>
          <span className="sp-stat__label">Restaurants</span>
        </div>
      </div>

      <h3 className="sp-subhead">Recent purchase orders</h3>
      {recentOrders.length === 0 ? (
        <SupplierEmptyState
          icon="fa-file-text-o"
          title="No purchase orders yet"
          message="When a restaurant sends you a PO, it will appear here."
          action={<Link to="/supplier/orders" className="sp-btn sp-btn--primary">View orders</Link>}
        />
      ) : (
        <ul className="sp-po-list">
          {recentOrders.map((po) => (
            <li key={`${po.restroId}-${po.id}`}>
              <Link to={`/supplier/orders/${po.restroId}/${po.id}`}>
                <div className="sp-po-list__meta">
                  <span className="sp-po-list__po">{po.poNumber}</span>
                  <span className="sp-po-list__restaurant">{po.restaurantName}</span>
                </div>
                <SupplierStatusBadge status={po.status} />
                <i className="fa fa-chevron-right" aria-hidden="true" style={{ color: '#9ca3af', fontSize: 12 }} />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {recentOrders.length > 0 && (
        <div className="sp-actions">
          <Link to="/supplier/orders" className="sp-btn sp-btn--primary">
            <i className="fa fa-list" aria-hidden="true" /> View all orders
          </Link>
        </div>
      )}
    </SupplierPageLayout>
  );
};

export default Dashboard;
